"use client";

import React, { useState, useRef, useLayoutEffect, ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

interface TooltipProps {
  /** The content to show inside the tooltip */
  text: string;
  /** The trigger element */
  children: ReactNode;
  /** Additional classes for the tooltip container */
  className?: string;
  /** Preferred placement */
  placement?: "top" | "bottom" | "left" | "right";
  /** Offset distance from the trigger */
  offset?: number;
}

export const Tooltip = ({
  text,
  children,
  className,
  placement = "top",
  offset = 12,
}: TooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentPlacement, setCurrentPlacement] = useState(placement);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Handle auto-placement adjustments on hover
  useLayoutEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let newPlacement = placement;

      // Simple flip logic
      if (
        placement === "top" &&
        triggerRect.top - tooltipRect.height - offset < 0
      ) {
        newPlacement = "bottom";
      } else if (
        placement === "bottom" &&
        triggerRect.bottom + tooltipRect.height + offset > viewportHeight
      ) {
        newPlacement = "top";
      } else if (
        placement === "left" &&
        triggerRect.left - tooltipRect.width - offset < 0
      ) {
        newPlacement = "right";
      } else if (
        placement === "right" &&
        triggerRect.right + tooltipRect.width + offset > viewportWidth
      ) {
        newPlacement = "left";
      }

      if (currentPlacement !== newPlacement) {
        requestAnimationFrame(() => {
          setCurrentPlacement(newPlacement);
        });
      }
    } else if (!isVisible && currentPlacement !== placement) {
      // Reset to default when closed
      requestAnimationFrame(() => {
        setCurrentPlacement(placement);
      });
    }
  }, [isVisible, placement, offset, currentPlacement]);

  // Framer Motion Variants for each direction
  const variants = {
    top: { opacity: 1, y: 0, x: "-50%", scale: 1 },
    bottom: { opacity: 1, y: 0, x: "-50%", scale: 1 },
    left: { opacity: 1, x: 0, y: "-50%", scale: 1 },
    right: { opacity: 1, x: 0, y: "-50%", scale: 1 },
    hidden: (place: string) => {
      switch (place) {
        case "top":
          return { opacity: 0, y: 4, x: "-50%", scale: 0.96 };
        case "bottom":
          return { opacity: 0, y: -4, x: "-50%", scale: 0.96 };
        case "left":
          return { opacity: 0, x: 4, y: "-50%", scale: 0.96 };
        case "right":
          return { opacity: 0, x: -4, y: "-50%", scale: 0.96 };
        default:
          return { opacity: 0 };
      }
    },
  };

  // Normalize offset to style for precision (since Tailwind classes might not match exact pixel offset)
  const getStyleParams = (place: string) => {
    switch (place) {
      case "top":
        return { bottom: "100%", left: "50%", marginBottom: offset };
      case "bottom":
        return { top: "100%", left: "50%", marginTop: offset };
      case "left":
        return { right: "100%", top: "50%", marginRight: offset };
      case "right":
        return { left: "100%", top: "50%", marginLeft: offset };
      default:
        return {};
    }
  };

  return (
    <div
      ref={triggerRef}
      className="relative inline-flex items-center justify-center group"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      onFocus={() => setIsVisible(true)}
      onBlur={() => setIsVisible(false)}
    >
      {children}

      <AnimatePresence mode="wait">
        {isVisible && (
          <motion.div
            ref={tooltipRef}
            custom={currentPlacement}
            variants={variants}
            initial="hidden"
            animate={currentPlacement}
            exit="hidden"
            transition={{ duration: 0.2, ease: "easeOut" }}
            style={getStyleParams(currentPlacement)}
            className={cn(
              "absolute z-50 whitespace-nowrap px-3 py-1.5",
              "bg-(--bg-primary) border border-(--border-subtle) rounded-lg shadow-sm", // Clean Premium Style
              "text-[10px] text-(--text-tertiary) font-mono tracking-widest uppercase opacity-90", // Typography
              "pointer-events-none flex items-center justify-center",
              className,
            )}
          >
            {/* Arrow */}
            <div
              className={cn(
                "absolute w-2.5 h-2.5 bg-(--bg-primary) border-(--border-subtle) rotate-45",
                currentPlacement === "top" &&
                  "top-full left-1/2 -translate-x-1/2 -mt-1.25 border-r border-b",
                currentPlacement === "bottom" &&
                  "bottom-full left-1/2 -translate-x-1/2 -mb-1.25 border-l border-t",
                currentPlacement === "left" &&
                  "left-full top-1/2 -translate-y-1/2 -ml-1.25 border-r border-t",
                currentPlacement === "right" &&
                  "right-full top-1/2 -translate-y-1/2 -mr-1.25 border-l border-b",
              )}
            />

            {text}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
